import { useEffect, useMemo, useRef } from 'react'
import { debounce } from 'lodash'

interface ICallback {
  callback: () => void
  delay?: number
}

/**
 * Creates a debounced function from the provided callback.
 *
 * This hook is designed to optimize scenarios where a function might be called
 * more frequently than desired. By debouncing the function, it ensures that
 * the callback is only invoked after a specified delay (250ms), which can
 * significantly improve performance in cases like handling user input, window resizing,
 * or API calls in response to user actions.
 *
 * The hook utilizes useRef to maintain a stable reference to the latest version
 * of the callback function without causing re-renders. It uses useEffect to update
 * this reference whenever the callback changes. The useMemo hook is used to memoize
 * the debounced version of the function, ensuring that it is not recreated on every
 * render, which provides a further performance optimization.
 *
 * The benefit of optimizing the callback in this way is that the callback can contain
 * a value that changes frequently, such as a state or prop, without causing the debounced
 * function to be recreated on every render.
 *
 * @param {ICallback} args - The arguments object.
 * @param {() => void} args.callback - The callback function to debounce.
 * @param {number} args.delay - The time in milliseconds to delay the callback execution.
 * @returns {() => void} A debounced version of the callback function that delays its execution.
 */
export const useDebounceCallback = ({ callback, delay = 250 }: ICallback) => {
  const ref = useRef<ICallback['callback']>()

  useEffect(() => {
    ref.current = callback
  }, [callback])

  const debouncedCallback = useMemo(() => {
    const func = () => {
      ref.current?.()
    }

    return debounce(func, delay)
  }, [])

  return debouncedCallback
}
